import { useMsal } from '@azure/msal-react';
import { Button, Text } from '@fluentui/react-components';
import { ArrowDownloadRegular } from '@fluentui/react-icons';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useStores } from '../../common/hooks';
import { AssignedNominationStatus, AwardJudging, ProgramRef } from '../../models';
import { RoutePaths } from '../../route-paths';
import { PageTitle } from '../shared-components';
import { CurrentWindow } from '../shared-components/CurrentWindow';
import Loading from '../shared-components/Loading';
import { Notifications } from '../shared-components/Notifications';
import { AwardJudgingBoxSection } from '../shared-components/award-judging-box-section/AwardJudgingBoxSection';
import { AllMediaDownloadModal } from '../shared-components/download-media-modal/AllMediaDownloadModal';
import { FlexBox } from '../shared-components/flex-box';
import NominationJudgedSummariesTable from '../shared-components/nomination-judged-summaries-table/NominationJudgedSummariesTable';
import './ProgramDashboardPage.scss';
import { AwardListAccordionContainer } from './components/AwardListAccordionContainer';
import { AwardProgramCharts } from './components/AwardProgramCharts';

const NO_NOMINATION_LEFT_TO_JUDGE_IN_AWARD = 'There are no nominations left for you to judge on this award at the moment. Please check back later.';
const NO_NOMINATION_LEFT_TO_JUDGE_IN_COUNTRY_AWARD = 'There are no nominations left for you to judge on this country award at the moment. Please check back later.'
const YOU_HAVE_COMPLETED_JUDGING_FOR_AWARD = 'You have completed your judging for this award. Thank you for your contribution!';
const YOU_HAVE_COMPLETED_JUDGING_FOR_COUNTRY_AWARD = 'You have completed your judging for this country award. Thank you for your contribution!';

const ProgramDashboardPageImpl: React.FC = () => {
  const {
    instance
  } = useMsal();

  const {
    apiStore,
    userStore,
    awardStore,
    notificationStore,
    programDashboardPageUiStore,
  } = useStores();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isShowDownloadModal, setShowDownloadModal] = useState<boolean>(false);

  const navigate = useNavigate();

  const shouldRenderAwardGroupSummaries = (): boolean => {
    return (userStore.isAdmin || userStore.isGroupLead);
  }

  const shouldRenderRegionAwardSummaries = (): boolean => {
    return (userStore.isAdmin ||
      userStore.isRegionLead ||
      userStore.isCountryLead ||
      userStore.isCountryNominationReviewer);
  }

  const shouldRenderAwardSummaries = (): boolean => {
    return (userStore.isAwardLead || userStore.isAwardNominationReviewer);
  };

  const handleAwardJudgingButtonClicked = async (awardJudging: AwardJudging): Promise<void> => {
    if (userStore.userId === undefined) return;

    programDashboardPageUiStore.clearAwardJudgingAlertMessage(awardJudging.awardId);

    setIsLoading(true);
    try {
      const nominationIdRef = await apiStore.getJudgeAssignedNomination(
        instance,
        awardJudging.awardId,
        userStore.userId,
      );

      switch (nominationIdRef.status) {
        case AssignedNominationStatus.Assigned:
          navigate(
            `${RoutePaths.Awards}/${awardJudging.awardId}${RoutePaths.Nominations}/${nominationIdRef.nominationId}`,
            { replace: true },
          );

          break;

        case AssignedNominationStatus.NoneAvailable:
          programDashboardPageUiStore.setAwardJudgingAlertMessage(
            awardJudging.awardId,
            NO_NOMINATION_LEFT_TO_JUDGE_IN_AWARD
          );

          break;

        case AssignedNominationStatus.JudgingComplete:
          programDashboardPageUiStore.setAwardJudgingAlertMessage(
            awardJudging.awardId,
            YOU_HAVE_COMPLETED_JUDGING_FOR_AWARD
          );

          break;

        default:
          return;
      }
    } catch (error) {
      // TODO - handle error
    } finally {
      setIsLoading(false);
    }
  }

  const handleCountryAwardJudgingButtonClicked = async (awardJudging: AwardJudging): Promise<void> => {
    if (userStore.userId === undefined || !awardJudging.countryId) return;

    programDashboardPageUiStore.clearCountryAwardJudgingAlertMessage(
      awardJudging.awardId,
      awardJudging.countryId,
    );

    setIsLoading(true);
    try {
      const nominationIdRef = await apiStore.getCountryJudgeAssignedNomination(
        instance,
        awardJudging.awardId,
        userStore.userId,
        awardJudging.countryId,
      );

      switch (nominationIdRef.status) {
        case AssignedNominationStatus.Assigned:
          navigate(
            `${RoutePaths.Awards}/${awardJudging.awardId}${RoutePaths.Nominations}/${nominationIdRef.nominationId}/${RoutePaths.Countries}/${awardJudging.countryId}`,
            { replace: true },
          );

          break;

        case AssignedNominationStatus.NoneAvailable:
          programDashboardPageUiStore.setCountryAwardJudgingAlertMessage(
            awardJudging.awardId,
            awardJudging.countryId,
            NO_NOMINATION_LEFT_TO_JUDGE_IN_COUNTRY_AWARD,
          );
          break;

        case AssignedNominationStatus.JudgingComplete:
          programDashboardPageUiStore.setCountryAwardJudgingAlertMessage(
            awardJudging.awardId,
            awardJudging.countryId,
            YOU_HAVE_COMPLETED_JUDGING_FOR_COUNTRY_AWARD,
          );
          break;

        default:
          return;
      }
    } catch (error) {
      // TODO - handle error
    } finally {
      setIsLoading(false);
    }
  }

  const getDefaultProgramRef = async (): Promise<ProgramRef | undefined> => {
    try {
      const defaultProgramRef = await apiStore.getDefaultProgramRef(instance);
      return defaultProgramRef;
    } catch (error) {
      // TODO - handle error
    }
  };

  const getNotifications = async (programId: number): Promise<void> => {
    try {
      const notifications = await apiStore.getProgramNotifications(instance, programId, 'program-dashboard');
      notificationStore.setNotifications('program-dashboard', notifications);
    } catch (error) {
      // TODO - handle error
    }
  };

  const getAwardProgramStatistic = async (programId: number): Promise<void> => {
    if (!(userStore.isAdmin || userStore.isRegionLead || userStore.isGroupLead)) return;

    try {
      const awardProgramStatistic = await apiStore.getAwardProgramStatistic(instance, programId);
      programDashboardPageUiStore.setAwardProgramStatistic(awardProgramStatistic);
    } catch (error) {
      // TODO - handle error
    }
  };

  const getAwardJudgings = async (): Promise<void> => {
    if (!userStore.isJudge || !awardStore.isJudgingWindowOpen) return;
    if (!userStore.userInfo) return;

    try {
      const awardJudgings = await apiStore.getAwardJudgings(instance, userStore.userInfo.userId);
      programDashboardPageUiStore.setAwardJudgings(awardJudgings);
    } catch (error) {
      // TODO - handle error
    }
  };

  const getNominationJudgedSummaries = async (): Promise<void> => {
    if (!userStore.userInfo) return;
    try {
      const nominationJudgedSummaries = await apiStore.getNominationJudgedSummaries(instance, userStore.userInfo.userId);
      programDashboardPageUiStore.setNominationJudgedSummaries(nominationJudgedSummaries);
    } catch (error) {
      // TODO - handle error
    }
  };

  const getGroups = async (): Promise<void> => {
    try {
      const groups = await apiStore.getGroups(instance);
      programDashboardPageUiStore.setGroups(groups);
    } catch (error) {
      // TODO - handle error
    }
  };

  const getProgramRegions = async (programId: number): Promise<void> => {
    try {
      const programRegions = await apiStore.getProgramRegions(instance, programId);
      programDashboardPageUiStore.setProgramRegions(programRegions);
    } catch (error) {
      // TODO - handle error
    }
  };

  const getAwardGroupsSummaries = async (programId: number): Promise<void> => {
    if (!shouldRenderAwardGroupSummaries()) return;

    try {
      const awardGroupsSummaries = await apiStore.getAwardGroupsSummaries(instance, programId);
      programDashboardPageUiStore.setAwardGroupsSummaries(awardGroupsSummaries);
    } catch (error) {
      // TODO - handle error
    }
  };

  const getRegionAwardSummaries = async (programId: number): Promise<void> => {
    if (!shouldRenderRegionAwardSummaries()) return;

    try {
      const regionAwardSummaries = await apiStore.getRegionAwardSummaries(instance, programId);
      programDashboardPageUiStore.setRegionAwardSummaries(regionAwardSummaries);
    } catch (error) {
      // TODO - handle error
    }
  };

  const getAwardSummaries = async (programId: number): Promise<void> => {
    if (!shouldRenderAwardSummaries()) return;

    try {
      const awardSummaries = await apiStore.getAwardSummaries(instance, programId);
      programDashboardPageUiStore.setAwardSummaries(awardSummaries);
    } catch (error) {
      // TODO - handle error
    }
  };

  const preloadData = async (): Promise<void> => {
    setIsLoading(true);

    const programRef = await getDefaultProgramRef();

    if (!programRef?.programId) {
      setIsLoading(false);
      return;
    }

    await Promise.allSettled([
      getNotifications(programRef.programId),
      getAwardProgramStatistic(programRef.programId),
      getAwardJudgings(),
      getNominationJudgedSummaries(),
      getGroups(),
      getProgramRegions(programRef.programId),
      getAwardGroupsSummaries(programRef.programId),
      getRegionAwardSummaries(programRef.programId),
      getAwardSummaries(programRef.programId),
    ]);

    setIsLoading(false);
  };

  useEffect(() => {
    preloadData();
    return () => {
      programDashboardPageUiStore.resetStates();
    };
  }, []);

  return (
    <FlexBox
      className='program-dashboard-page'
      direction='column'
    >
      {isLoading && <Loading />}

      <FlexBox
        className='program-dashboard-page__header'
        content='space-between'
      >
        <div>
          <PageTitle
            subtitle='Microsoft AI Cloud Partner Program'
            title={awardStore.defaultProgramRef?.displayName ?? ''}
          />
          <CurrentWindow />
        </div>
        <FlexBox
          direction='column'
          aligns='end'
        >{
            userStore.isAdmin && awardStore.isWinnersWindowOpen && (
              <Button
                appearance='transparent'
                icon={<ArrowDownloadRegular />}
                onClick={() => setShowDownloadModal(!isShowDownloadModal)}
              >
                Download media
              </Button>
            )
          }
          <AwardProgramCharts />
        </FlexBox>
      </FlexBox>
      {
        notificationStore.dashboardPageNotifications('program-dashboard').length > 0 &&
        <Notifications
          notifications={notificationStore.dashboardPageNotifications('program-dashboard')}
        />
      }

      <Text
        as='h2'
        size={500}
        weight='semibold'
      >
        Awards you own
      </Text>
      <AwardListAccordionContainer
        shouldRenderAwardGroupSummaries={shouldRenderAwardGroupSummaries()}
        shouldRenderRegionAwardSummaries={shouldRenderRegionAwardSummaries()}
        shouldRenderAwardSummaries={shouldRenderAwardSummaries()}
      />
      {
        awardStore.isJudgingWindowOpen &&
        <>
          {
            userStore.isJudge && programDashboardPageUiStore.hasJudgings &&
            <AwardJudgingBoxSection
              awardJudgings={programDashboardPageUiStore.awardJudgings}
              countryAwardJudgings={programDashboardPageUiStore.countryAwardJudgings}
              awardAlertMessages={programDashboardPageUiStore.awardJudgingAlertMessages}
              countryAwardAlertMessages={programDashboardPageUiStore.countryAwardJudgingAlertMessages}
              handleAwardJudgingButtonClicked={handleAwardJudgingButtonClicked}
              handleCountryAwardJudgingButtonClicked={handleCountryAwardJudgingButtonClicked}
            />
          }
          {
            userStore.isJudge && programDashboardPageUiStore.hasNominationJudgedSummaries &&
            <NominationJudgedSummariesTable
              nominationJudgedSummaries={programDashboardPageUiStore.nominationJudgedSummaries}
            />
          }
        </>
      }
      {
        isShowDownloadModal &&
        <AllMediaDownloadModal
          groups={programDashboardPageUiStore.groups}
          regions={programDashboardPageUiStore.programRegions}
          onCloseModal={() => setShowDownloadModal(false)}
          onDownloadAll={() => {
            setShowDownloadModal(false);
          }}
          onDownloadByGroupRegion={(downloadCategoryType, ids) => {
            setShowDownloadModal(false);
          }}
        />
      }
    </FlexBox>
  );
}

export const ProgramDashboardPage = observer(ProgramDashboardPageImpl);
